import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { isAdmin } from '../../services/authService';
import { 
  getActivityDetails, 
  getSuspiciousActivities, 
  ActivityDetail, 
  bulkUpdateActivitySuspiciousStatus, 
  updateActivitySuspiciousStatus,
  getTournaments,
  Tournament
} from '../../services/activitiesService';
import { FaFlag, FaCheck, FaTimes, FaFilter, FaSortAmountDown, FaSortAmountUp, FaCalendarAlt } from 'react-icons/fa';
import DatePickerCustom from '../../components/DatePickerCustom';
import './styles.css';
import { useForm, Controller } from 'react-hook-form';

interface FormValues {
  startDate: string;
  endDate: string;
}

const FlagManagement: React.FC = () => {
  const [activities, setActivities] = useState<ActivityDetail[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ActivityDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedActivities, setSelectedActivities] = useState<number[]>([]);
  const [onlySuspicious, setOnlySuspicious] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<number>(0);
  const [loadingTournaments, setLoadingTournaments] = useState<boolean>(true);
  
  // Thêm state để lưu trữ ngày bắt đầu và ngày kết thúc
  const [dateRange, setDateRange] = useState<{startDate: string | null, endDate: string | null}>({
    startDate: null,
    endDate: null
  });
  
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    sortBy: 'startDateLocal',
    sortDir: 'desc',
    startDate: '',
    endDate: ''
  });

  // Khởi tạo react-hook-form
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      startDate: '',
      endDate: ''
    }
  });

  // Lấy danh sách giải đấu khi component mount
  useEffect(() => {
    const fetchTournaments = async () => {
      setLoadingTournaments(true);
      try {
        const tournamentsList = await getTournaments();
        setTournaments(tournamentsList);
        
        // Nếu có giải đấu, chọn giải đấu đầu tiên
        if (tournamentsList.length > 0) {
          setSelectedTournament(tournamentsList[0].id);
        }
      } catch (err) {
        console.error('Lỗi khi lấy danh sách giải đấu:', err);
        setError('Không thể lấy danh sách giải đấu');
      } finally {
        setLoadingTournaments(false);
      }
    };

    if (isAdmin()) {
      fetchTournaments();
    }
  }, []);
  
  // Lấy hoạt động đáng ngờ khi thay đổi bộ lọc hoặc giải đấu
  useEffect(() => {
    if (isAdmin() && selectedTournament > 0) {
      fetchSuspiciousActivities();
    }
  }, [filters, selectedTournament]);

  useEffect(() => {
    applyFilters();
  }, [activities, onlySuspicious]);

  const fetchSuspiciousActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const response = await getSuspiciousActivities(selectedTournament, {
        page: filters.page,
        size: filters.size,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
        startDate: filters.startDate,  // Thêm ngày bắt đầu vào request
        endDate: filters.endDate       // Thêm ngày kết thúc vào request
      });
      
      if (response.result === 'SUCCESS') {
        setActivities(response.data.content);
        setTotalElements(response.data.totalElements);
        setTotalPages(response.data.totalPages);
      } else {
        setError(response.message || 'Có lỗi xảy ra khi lấy dữ liệu');
      }
    } catch (err: any) {
      console.error('Lỗi khi lấy danh sách hoạt động đáng ngờ:', err);
      setError(err.message || 'Không thể lấy danh sách hoạt động đáng ngờ');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...activities];
    
    if (onlySuspicious) {
      filtered = filtered.filter(activity => activity.suspiciousActivity === 1);
    }
    
    setFilteredActivities(filtered);
  };

  const handleSortChange = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === 'desc' ? 'asc' : 'desc',
      page: 0
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleResetFilters = () => {
    setOnlySuspicious(false);
    setDateRange({
      startDate: null,
      endDate: null
    });
    setFilters({
      page: 0,
      size: 20,
      sortBy: 'startDateLocal',
      sortDir: 'desc',
      startDate: '',
      endDate: ''
    });
  };

  const handleSuspiciousFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnlySuspicious(e.target.checked);
  };

  const handleTournamentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTournament(Number(e.target.value));
  };
  
  const handleSelectActivity = (activityId: number) => {
    setSelectedActivities(prev => {
      if (prev.includes(activityId)) {
        return prev.filter(id => id !== activityId);
      } else {
        return [...prev, activityId];
      }
    });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedActivities(filteredActivities.map(activity => activity.id));
    } else {
      setSelectedActivities([]);
    }
  };

  // Hàm xử lý khi thay đổi ngày
  const handleDateChange = (type: 'startDate' | 'endDate', date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : '';
    
    setDateRange(prev => ({
      ...prev,
      [type]: dateStr
    }));

    // Cập nhật filters chỉ khi có sự thay đổi
    if (type === 'startDate' && dateStr !== filters.startDate) {
      setFilters(prev => ({
        ...prev,
        startDate: dateStr,
        page: 0
      }));
    } else if (type === 'endDate' && dateStr !== filters.endDate) {
      setFilters(prev => ({
        ...prev,
        endDate: dateStr,
        page: 0
      }));
    }
  };

  // Hàm xử lý submit form
  const onSubmitForm = (data: FormValues) => {
    setFilters(prev => ({
      ...prev,
      startDate: data.startDate,
      endDate: data.endDate,
      page: 0
    }));
  };

  const handleMarkSuspicious = async () => {
    if (selectedActivities.length === 0) {
      setError('Vui lòng chọn ít nhất một hoạt động để cập nhật');
      return;
    }

    try {
      setIsActionLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const result = await bulkUpdateActivitySuspiciousStatus(selectedActivities, 1);
      
      if (result.result === 'SUCCESS') {
        setSuccessMessage(result.message);
        setActivities(prev => prev.map(activity => {
          if (selectedActivities.includes(activity.id)) {
            return { ...activity, suspiciousActivity: 1 };
          }
          return activity;
        }));
        setSelectedActivities([]);
      } else {
        setError(result.message || 'Không thể cập nhật trạng thái hoạt động');
      }
    } catch (err: any) {
      console.error('Lỗi khi cập nhật trạng thái hoạt động:', err);
      setError(err.message || 'Không thể cập nhật trạng thái hoạt động');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRemoveSuspicious = async () => {
    if (selectedActivities.length === 0) {
      setError('Vui lòng chọn ít nhất một hoạt động để cập nhật');
      return;
    }

    try {
      setIsActionLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      const result = await bulkUpdateActivitySuspiciousStatus(selectedActivities, 0);
      
      if (result.result === 'SUCCESS') {
        setSuccessMessage(result.message);
        setActivities(prev => prev.map(activity => {
          if (selectedActivities.includes(activity.id)) {
            return { ...activity, suspiciousActivity: 0 };
          }
          return activity;
        }));
        setSelectedActivities([]);
      } else {
        setError(result.message || 'Không thể cập nhật trạng thái hoạt động');
      }
    } catch (err: any) {
      console.error('Lỗi khi cập nhật trạng thái hoạt động:', err);
      setError(err.message || 'Không thể cập nhật trạng thái hoạt động');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleToggleSuspicious = async (activityId: number, status: 0 | 1) => {
    try {
      setError(null);
      setSuccessMessage(null);
      
      const result = await updateActivitySuspiciousStatus(activityId, status);
      
      if (result.result === 'SUCCESS') {
        setActivities(prev => prev.map(activity => {
          if (activity.id === activityId) {
            return { ...activity, suspiciousActivity: status };
          }
          return activity;
        }));
        setSuccessMessage(`Đã cập nhật trạng thái hoạt động #${activityId} thành công`);
      } else {
        setError(result.message || 'Không thể cập nhật trạng thái hoạt động');
      }
    } catch (err: any) {
      console.error('Lỗi khi cập nhật trạng thái hoạt động:', err);
      setError(err.message || 'Không thể cập nhật trạng thái hoạt động');
    }
  };

  // Hàm hỗ trợ định dạng
  const formatDate = (dateArray: number[]) => {
    if (!dateArray || dateArray.length < 3) return 'N/A';
    const [year, month, day, hour, minute] = dateArray;
    return `${day}/${month}/${year} ${hour !== undefined && minute !== undefined ? `${hour}:${minute < 10 ? '0' + minute : minute}` : ''}`;
  };

  const formatDistance = (distance: number) => {
    if (distance < 1000) {
      return `${distance.toFixed(0)} m`;
    }
    return `${(distance / 1000).toFixed(2)} km`;
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatSpeed = (speed: number) => {
    if (!speed) return 'N/A';
    
    // Chuyển đổi m/s sang km/h
    const speedKmh = speed * 3.6;
    
    if (speedKmh < 10) {
      // Làm tròn 2 chữ số thập phân cho tốc độ nhỏ
      return `${speedKmh.toFixed(2)} km/h`;
    } else {
      // Làm tròn 1 chữ số thập phân cho tốc độ lớn
      return `${speedKmh.toFixed(1)} km/h`;
    }
  };

  const getActivityTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'run':
        return 'green';
      case 'ride':
        return 'blue';
      case 'swim':
        return 'cyan';
      case 'workout':
        return 'orange';
      case 'walk':
        return 'purple';
      default:
        return 'gray';
    }
  };

  // Kiểm tra quyền admin
  if (!isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-fluid p-4">
      <div className="page-header">
        <h1 className="page-title">Quản lý gán cờ</h1>
        <p className="page-subtitle">Đánh dấu và quản lý các hoạt động đáng nghi ngờ</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      <div className="flag-management-actions">
        <div className="action-buttons">
          <button
            className="action-btn mark-suspicious-btn icon-only-btn" 
            onClick={handleMarkSuspicious}
            disabled={isActionLoading || selectedActivities.length === 0}
            title="Đánh dấu đáng nghi ngờ"
          >
            <FaFlag />
            <span className="badge count-badge">{selectedActivities.length}</span>
          </button>
          <button
            className="action-btn remove-suspicious-btn icon-only-btn"
            onClick={handleRemoveSuspicious}
            disabled={isActionLoading || selectedActivities.length === 0}
            title="Gỡ đánh dấu đáng nghi ngờ"
          >
            <FaTimes />
            <span className="badge count-badge">{selectedActivities.length}</span>
          </button>
          
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="suspicious-filter"
              checked={onlySuspicious}
              onChange={handleSuspiciousFilterChange}
            />
            <label htmlFor="suspicious-filter">Chỉ hiển thị hoạt động đáng nghi ngờ</label>
          </div>
          
          <div className="select-all-wrapper">
            <input
              type="checkbox"
              id="select-all"
              checked={selectedActivities.length === filteredActivities.length && filteredActivities.length > 0}
              onChange={handleSelectAll}
            />
            <label htmlFor="select-all">Chọn tất cả</label>
          </div>
        </div>
        
        <div className="filter-container">
          <div className="filter-header">
            <h3 className="filter-title"><FaFilter /> Bộ lọc tìm kiếm</h3>
          </div>
          <div className="filter-content">
            {/* Form chọn ngày và giải đấu */}
            <form onSubmit={handleSubmit(onSubmitForm)}>
              <div className="filter-row">
                <div className="filter-item">
                  <label htmlFor="tournament-select">Giải đấu</label>
                  {loadingTournaments ? (
                    <div className="loading-text">Đang tải danh sách giải đấu...</div>
                  ) : (
                    <select 
                      id="tournament-select" 
                      className="filter-input tournament-select"
                      value={selectedTournament}
                      onChange={handleTournamentChange}
                    >
                      {tournaments.length === 0 ? (
                        <option value="0">Không có giải đấu nào</option>
                      ) : (
                        tournaments.map(tournament => (
                          <option key={tournament.id} value={tournament.id}>
                            {tournament.name}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
                
                <div className="filter-date-range">
                  <div className="filter-item">
                    <label htmlFor="start-date"><FaCalendarAlt /> Từ ngày</label>
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <DatePickerCustom
                          id="start-date"
                          selected={dateRange.startDate ? new Date(dateRange.startDate) : null}
                          onChange={(date: Date | null) => {
                            const dateStr = date ? date.toISOString().split('T')[0] : '';
                            field.onChange(dateStr);
                            handleDateChange('startDate', date);
                          }}
                          className="filter-input date-input"
                          placeholderText="DD/MM/YYYY"
                          dateFormat="dd/MM/yyyy"
                        />
                      )}
                    />
                  </div>
                  
                  <div className="filter-item">
                    <label htmlFor="end-date"><FaCalendarAlt /> Đến ngày</label>
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <DatePickerCustom
                          id="end-date"
                          selected={dateRange.endDate ? new Date(dateRange.endDate) : null}
                          onChange={(date: Date | null) => {
                            const dateStr = date ? date.toISOString().split('T')[0] : '';
                            field.onChange(dateStr);
                            handleDateChange('endDate', date);
                          }}
                          className="filter-input date-input"
                          placeholderText="DD/MM/YYYY"
                          dateFormat="dd/MM/yyyy"
                          minDate={dateRange.startDate ? new Date(dateRange.startDate) : undefined}
                        />
                      )}
                    />
                  </div>
                </div>
                
                <div className="filter-actions">
                  <button 
                    type="submit" 
                    className="filter-button apply-button"
                  >
                    <span>Áp dụng</span>
                  </button>
                  <button 
                    type="button" 
                    className="filter-button reset-button"
                    onClick={handleResetFilters}
                  >
                    <span>Đặt lại</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-indicator">Đang tải dữ liệu...</div>
      ) : (
        <div className="activity-list-container">
          {filteredActivities.length === 0 ? (
            <div className="empty-state">
              <p>Không có hoạt động nào được tìm thấy</p>
            </div>
          ) : (
            <>
              <table className="activity-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>Chọn</th>
                    <th>
                      <div className="sort-header" onClick={() => handleSortChange('startDateLocal')}>
                        <span>Ngày</span>
                        {filters.sortBy === 'startDateLocal' && (
                          filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                        )}
                      </div>
                    </th>
                    <th>Người dùng</th>
                    <th>
                      <div className="sort-header" onClick={() => handleSortChange('name')}>
                        <span>Tên hoạt động</span>
                        {filters.sortBy === 'name' && (
                          filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                        )}
                      </div>
                    </th>
                    <th>Loại</th>
                    <th>
                      <div className="sort-header" onClick={() => handleSortChange('distance')}>
                        <span>Khoảng cách</span>
                        {filters.sortBy === 'distance' && (
                          filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                        )}
                      </div>
                    </th>
                    <th>
                      <div className="sort-header" onClick={() => handleSortChange('movingTime')}>
                        <span>Thời gian</span>
                        {filters.sortBy === 'movingTime' && (
                          filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                        )}
                      </div>
                    </th>
                    <th>
                      <div className="sort-header" onClick={() => handleSortChange('averageSpeed')}>
                        <span>Tốc độ</span>
                        {filters.sortBy === 'averageSpeed' && (
                          filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                        )}
                      </div>
                    </th>
                    <th>Trạng thái</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((activity) => (
                    <tr 
                      key={activity.id}
                      className={activity.suspiciousActivity === 1 ? 'suspicious-row' : ''}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedActivities.includes(activity.id)}
                          onChange={() => handleSelectActivity(activity.id)}
                        />
                      </td>
                      <td>{formatDate(activity.startDateLocal)}</td>
                      <td>
                        <div className="user-info">
                          <img 
                            src={activity.userProfileImageUrl || '/assets/default-avatar.png'} 
                            alt={`${activity.userFirstName} ${activity.userLastName}`}
                            className="user-avatar"
                          />
                          <span>{activity.userFirstName} {activity.userLastName}</span>
                        </div>
                      </td>
                      <td>
                        {activity.name}
                        {activity.suspiciousActivity === 1 && (
                          <FaFlag className="suspicious-flag" title="Hoạt động đáng nghi ngờ" />
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${getActivityTypeColor(activity.type)}`}>
                          {activity.type}
                        </span>
                      </td>
                      <td>{formatDistance(activity.distance)}</td>
                      <td>{formatTime(activity.movingTime)}</td>
                      <td>{formatSpeed(activity.averageSpeed)}</td>
                      <td>
                        <span className={`badge ${activity.suspiciousActivity === 1 ? 'badge-suspicious' : 'badge-normal'}`}>
                          {activity.suspiciousActivity === 1 ? 'Đáng nghi ngờ' : 'Bình thường'}
                        </span>
                      </td>
                      <td className="action-cell">
                        {activity.suspiciousActivity === 1 ? (
                          <button
                            className="action-btn-small remove-flag-btn"
                            onClick={() => handleToggleSuspicious(activity.id, 0)}
                            title="Gỡ đánh dấu"
                          >
                            <FaCheck />
                          </button>
                        ) : (
                          <button
                            className="action-btn-small add-flag-btn"
                            onClick={() => handleToggleSuspicious(activity.id, 1)}
                            title="Đánh dấu đáng nghi ngờ"
                          >
                            <FaFlag />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 0 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Hiển thị {filteredActivities.length} trong tổng số {totalElements} hoạt động
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn" 
                      disabled={filters.page === 0}
                      onClick={() => handlePageChange(filters.page - 1)}
                    >
                      <span>&laquo;</span>
                    </button>
                    <span className="pagination-text">
                      Trang {filters.page + 1} / {totalPages}
                    </span>
                    <button 
                      className="pagination-btn" 
                      disabled={filters.page >= totalPages - 1}
                      onClick={() => handlePageChange(filters.page + 1)}
                    >
                      <span>&raquo;</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FlagManagement; 